import React from 'react';
import styled from 'styled-components';
import {Button, FlexRow, Icon, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';

const tokens = tokensForThemeProvider.sky.sys;
const compTokens = tokensForThemeProvider.sky.comp;

/** Same error-alert shell `AIBanner`/`PublishStateBanner` use for their own error states, but
 * full-bleed: this one is rendered outside the surface's padded wrapper so it spans the content
 * area edge to edge, directly under the global navigation and above the page header.
 *
 * Deliberately not the design system's `Alert`, which is the reuse `.claude/rules/design-system.md`
 * would otherwise ask for: `Alert` draws a border on all four sides and a border radius, which is
 * exactly what full-bleed placement must not have. Its palette IS reused rather than re-invented —
 * every colour below comes from the same `comp.alerts` error tokens `Alert` resolves through
 * `AlertType.ERROR`, so the two stay in step. */
const BannerWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: ${tokens.spacing['200']} ${tokens.spacing['400']} ${tokens.spacing['200']} ${tokens.spacing['600']};
	background: ${compTokens.alerts.color.background.error};
	border-left: 12px solid ${compTokens.alerts.color.bar.error};
	box-shadow: 0 2px 4px rgba(35, 35, 52, 0.12);
`;

/** Reports that this tab held the edit lock and no longer does, and offers a page reload as the way
 * back into editing. Renders nothing for 'unlocked', 'held' and 'blocked': a blocked editor is told
 * by the toolbar's helper text instead, and there is nothing left for this banner to say once the
 * lock is held or free.
 *
 * The unsaved working copy stays on screen while this banner stands, and reloading discards it. That
 * is deliberate, not an oversight: the copy states the expiry, states that the changes can no longer
 * be saved, and asks the user to note anything they want to keep BEFORE reloading. It is the one
 * sanctioned exception to the rule that a control must never be the only escape from a state when
 * pressing it discards unsaved work — here nothing can save that work any more, whatever the user
 * does, so the honest offer is a clean restart rather than a retry that cannot restore savability
 * on its own. Do not "fix" this by removing the reload or by reloading automatically; the pause in
 * which the user copies their changes out is the point.
 *
 * There is deliberately no in-place retry. Re-acquiring the lock without a reload leaves a working
 * copy on screen that may already be behind what another editor stored, and only a fresh load is
 * guaranteed to show what is stored — the same reasoning `PublishStateBanner` applies to its own
 * out-of-step state. */
export function LockStateBanner(): JSX.Element | null {
	const {formatMessage} = useIntl();
	const {lockState} = useReportBuilder();

	if (lockState !== 'lost') {
		return null;
	}

	return (
		<BannerWrapper data-cy={'ai-report-lock-state-banner'}>
			<FlexRow alignItems={'center'} gap={'s'}>
				{/* `customColor`, not `color`: `color` is a lookup into a named token map, so a raw hex is
				    dropped and propTypes logs an invalid prop. Same token the DS `Alert` uses for its own
				    error glyph — `semanticicon.error`, not the `bar.error` accent-bar colour. */}
				<Icon icon={'error'} size={'s'} customColor={compTokens.alerts.color.semanticicon.error} />
				<Text color={compTokens.alerts.color.text.error}>{formatMessage({id: 'ai_report_builder.lock_lost'})}</Text>
			</FlexRow>
			<Button
				emphasis={'low'}
				icon={'reload'}
				onPress={() => window.location.reload()}
				data-cy={'ai-report-reload-page-button'}
			>
				{formatMessage({id: 'ai_report_builder.lock_reload'})}
			</Button>
		</BannerWrapper>
	);
}
